// WatermarkSettings - Global watermark configuration for document exports
import React, { useState, createContext, useContext, useEffect } from 'react';
import { Settings2, Image, Type, X } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Slider } from '../../ui/slider';
import { downscaleImageToDataURL } from '../../../utils/imageUtils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import { Switch } from '../../ui/switch';

// Context for watermark settings
const WatermarkContext = createContext(null);

export function useWatermark() {
  return useContext(WatermarkContext);
}

export function WatermarkProvider({ children }) {
  const [watermarkConfig, setWatermarkConfig] = useState(() => {
    // Load from localStorage
    const saved = localStorage.getItem('keyMadeWatermarkConfig');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        return null;
      }
    }
    return null;
  });

  // Save to localStorage when config changes
  useEffect(() => {
    if (watermarkConfig) {
      localStorage.setItem('keyMadeWatermarkConfig', JSON.stringify(watermarkConfig));
    } else {
      localStorage.removeItem('keyMadeWatermarkConfig');
    }
  }, [watermarkConfig]);

  return (
    <WatermarkContext.Provider value={{ watermarkConfig, setWatermarkConfig }}>
      {children}
    </WatermarkContext.Provider>
  );
}

export function WatermarkSettingsDialog({ darkMode = false, trigger }) {
  const { watermarkConfig, setWatermarkConfig } = useWatermark() || {};
  const [open, setOpen] = useState(false);
  const [enabled, setEnabled] = useState(watermarkConfig?.enabled || false);
  const [type, setType] = useState(watermarkConfig?.type || 'text');
  const [text, setText] = useState(watermarkConfig?.text || 'DRAFT');
  const [opacity, setOpacity] = useState(watermarkConfig?.opacity || 30);
  const [position, setPosition] = useState(watermarkConfig?.position || 'diagonal');
  const [fontSize, setFontSize] = useState(watermarkConfig?.fontSize || 60);
  const [color, setColor] = useState(watermarkConfig?.color || '#CCCCCC');
  const [imageData, setImageData] = useState(watermarkConfig?.imageData || '');
  const [imageWidth, setImageWidth] = useState(watermarkConfig?.imageWidth || 80);
  const [imageHeight, setImageHeight] = useState(watermarkConfig?.imageHeight || 40);
  const [rotation, setRotation] = useState(watermarkConfig?.rotation ?? -45);

  const handleSave = () => {
    if (enabled) {
      setWatermarkConfig({
        enabled,
        type,
        text,
        opacity,
        position,
        fontSize,
        color,
        imageData,
        imageWidth,
        imageHeight,
        rotation
      });
    } else {
      setWatermarkConfig(null);
    }
    setOpen(false);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      downscaleImageToDataURL(file, { maxEdge: 512 })
        .then((dataUrl) => setImageData(dataUrl))
        .catch(() => { /* silently ignore — watermark stays unchanged */ });
    }
  };

  const presetTexts = ['DRAFT', 'CONFIDENTIAL', 'FOR REVIEW', 'PRELIMINARY', 'NOT FOR CONSTRUCTION', 'SAMPLE'];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button 
            variant="ghost" 
            size="sm"
            className={`gap-2 ${darkMode ? 'hover:bg-gray-800' : ''}`}
            data-testid="watermark-settings-btn"
          >
            <Settings2 className="w-4 h-4" />
            Watermark
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className={`max-w-md ${darkMode ? 'bg-[#1A1A1A] border-gray-700 text-white' : ''}`}>
        <DialogHeader>
          <DialogTitle className={darkMode ? 'text-white' : ''}>Document Watermark Settings</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 pt-4">
          {/* Enable Toggle */}
          <div className="flex items-center justify-between">
            <Label className={darkMode ? 'text-gray-300' : ''}>Enable Watermark</Label>
            <Switch 
              checked={enabled} 
              onCheckedChange={setEnabled}
              data-testid="watermark-toggle"
            />
          </div>

          {enabled && (
            <>
              {/* Watermark Type */}
              <div className="space-y-2">
                <Label className={darkMode ? 'text-gray-300' : ''}>Watermark Type</Label>
                <div className="flex gap-2">
                  <Button
                    variant={type === 'text' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setType('text')}
                    className={`flex-1 ${darkMode && type !== 'text' ? 'border-gray-600 hover:bg-gray-800' : ''}`}
                  >
                    <Type className="w-4 h-4 mr-2" />
                    Text
                  </Button>
                  <Button
                    variant={type === 'image' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setType('image')}
                    className={`flex-1 ${darkMode && type !== 'image' ? 'border-gray-600 hover:bg-gray-800' : ''}`}
                  >
                    <Image className="w-4 h-4 mr-2" />
                    Logo/Image
                  </Button>
                </div>
              </div>

              {/* Text Settings */}
              {type === 'text' && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className={darkMode ? 'text-gray-300' : ''}>Watermark Text</Label>
                    <Input 
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      placeholder="Enter watermark text..."
                      className={darkMode ? 'bg-[#2A2A2A] border-gray-600 text-white' : ''}
                    />
                    <div className="flex flex-wrap gap-1 mt-2">
                      {presetTexts.map(preset => (
                        <Button
                          key={preset}
                          variant="outline"
                          size="sm"
                          className={`text-xs px-2 py-1 h-auto ${darkMode ? 'border-gray-600 hover:bg-gray-800' : ''}`}
                          onClick={() => setText(preset)}
                        >
                          {preset}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className={darkMode ? 'text-gray-300' : ''}>Font Size</Label>
                      <Input 
                        type="number"
                        value={fontSize}
                        onChange={(e) => setFontSize(Number(e.target.value))}
                        min="20"
                        max="120"
                        className={darkMode ? 'bg-[#2A2A2A] border-gray-600 text-white' : ''}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className={darkMode ? 'text-gray-300' : ''}>Color</Label>
                      <div className="flex gap-2">
                        <Input 
                          type="color"
                          value={color}
                          onChange={(e) => setColor(e.target.value)}
                          className="w-12 h-9 p-1 cursor-pointer"
                        />
                        <Input 
                          value={color}
                          onChange={(e) => setColor(e.target.value)}
                          className={`flex-1 ${darkMode ? 'bg-[#2A2A2A] border-gray-600 text-white' : ''}`}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Image Settings */}
              {type === 'image' && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className={darkMode ? 'text-gray-300' : ''}>Upload Logo/Image</Label>
                    <Input 
                      type="file"
                      accept="image/png,image/jpeg,image/gif"
                      onChange={handleImageUpload}
                      className={darkMode ? 'bg-[#2A2A2A] border-gray-600 text-white' : ''}
                    />
                    {imageData && (
                      <div className="mt-2 p-2 border rounded relative">
                        <img 
                          src={imageData} 
                          alt="Watermark preview" 
                          className="max-h-20 mx-auto"
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          className="absolute top-1 right-1 h-6 w-6 p-0"
                          onClick={() => setImageData('')}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className={darkMode ? 'text-gray-300' : ''}>Width (mm)</Label>
                      <Input 
                        type="number"
                        value={imageWidth}
                        onChange={(e) => setImageWidth(Number(e.target.value))}
                        min="20"
                        max="150"
                        className={darkMode ? 'bg-[#2A2A2A] border-gray-600 text-white' : ''}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className={darkMode ? 'text-gray-300' : ''}>Height (mm)</Label>
                      <Input 
                        type="number"
                        value={imageHeight}
                        onChange={(e) => setImageHeight(Number(e.target.value))}
                        min="10"
                        max="100"
                        className={darkMode ? 'bg-[#2A2A2A] border-gray-600 text-white' : ''}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Opacity Control */}
              <div className="space-y-2">
                <div className="flex justify-between">
                  <Label className={darkMode ? 'text-gray-300' : ''}>Opacity</Label>
                  <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>{opacity}%</span>
                </div>
                <Slider 
                  value={[opacity]}
                  onValueChange={(v) => setOpacity(v[0])}
                  min={5}
                  max={100}
                  step={5}
                  className="py-2"
                />
              </div>

              {/* Position */}
              <div className="space-y-2">
                <Label className={darkMode ? 'text-gray-300' : ''}>Position</Label>
                <Select value={position} onValueChange={setPosition}>
                  <SelectTrigger className={darkMode ? 'bg-[#2A2A2A] border-gray-600 text-white' : ''}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}>
                    <SelectItem value="diagonal">Diagonal (across page)</SelectItem>
                    <SelectItem value="center">Center</SelectItem>
                    <SelectItem value="top">Top</SelectItem>
                    <SelectItem value="bottom">Bottom</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Rotation */}
              <div className="space-y-2">
                <div className="flex justify-between">
                  <Label className={darkMode ? 'text-gray-300' : ''}>Rotation</Label>
                  <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>{rotation}°</span>
                </div>
                <Slider 
                  value={[rotation]}
                  onValueChange={(v) => setRotation(v[0])}
                  min={-90}
                  max={90}
                  step={5}
                  className="py-2"
                  data-testid="watermark-rotation-slider"
                />
                <div className={`flex justify-between text-xs ${darkMode ? 'text-gray-500' : 'text-muted-foreground'}`}>
                  <span>-90°</span>
                  <button 
                    className={`px-2 py-0.5 rounded text-xs ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                    onClick={() => setRotation(0)}
                  >
                    0° (flat)
                  </button>
                  <span>90°</span>
                </div>
              </div>
            </>
          )}

          {/* Save Button */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button 
              variant="outline" 
              onClick={() => setOpen(false)}
              className={darkMode ? 'border-gray-600 hover:bg-gray-800' : ''}
            >
              Cancel
            </Button>
            <Button onClick={handleSave} data-testid="save-watermark-btn">
              Save Settings
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default WatermarkSettingsDialog;
